/**
 * MapLibre GL JS container using react-map-gl.
 */

import { useRef, useCallback, useEffect, useState } from 'react';
import Map, { NavigationControl, AttributionControl, GeolocateControl } from 'react-map-gl/maplibre';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

import { MAP_CONFIG } from '../../config';
import { useResolvedTheme } from '../../hooks/useResolvedTheme';
import { useViewport } from '../../hooks/useViewport';
import { useFilterStore } from '../../stores/filterStore';
import { useAuthStore } from '../../stores/authStore';
import { useMapCommandStore } from '../../stores/mapCommandStore';
import { registerVehicleIcons, resetIconRegistration, loadIconOnDemand, KNOWN_ICON_IDS } from '../../utils/vehicleIcons';
import { VehicleLayer } from './VehicleLayer';
import { V4PositionLayer } from './V4PositionLayer';
import { DeviationLine } from './DeviationLine';
import { V5PositionLayer } from './V5PositionLayer';
import { V1DAPositionLayer } from './V1DAPositionLayer';
import { HexDensityLayer } from './HexDensityLayer';
import { HexDensityLegend } from './HexDensityLegend';
import { PollerBboxLayer } from './PollerBboxLayer';
import { RouteLayer } from './RouteLayer';
import { PositionSourcesLayer } from './PositionSourcesLayer';

import type { MapRef, ViewStateChangeEvent } from 'react-map-gl/maplibre';

export interface MapContainerProps {
  /** Additional CSS class */
  className?: string;
}

export function MapContainer({ className = '' }: MapContainerProps) {
  const mapRef = useRef<MapRef | null>(null);
  const { updateBounds, updateZoom, forceSubscribe } = useViewport();
  const setZoomLevel = useFilterStore((state) => state.setZoomLevel);
  const isAdmin = useAuthStore((state) => state.user)?.role === 'admin';
  const resolvedTheme = useResolvedTheme();
  const mapStyle = resolvedTheme === 'dark' ? MAP_CONFIG.DARK_STYLE_URL : MAP_CONFIG.STYLE_URL;

  // Track when vehicle icons have been registered with the map
  const [iconsReady, setIconsReady] = useState(false);

  // Track whether initial viewport subscription has been sent
  const initializedRef = useRef(false);

  // Keep latest callbacks in refs so the initialization logic always uses
  // current values without needing unstable callback identities.
  const forceSubscribeRef = useRef(forceSubscribe);
  forceSubscribeRef.current = forceSubscribe;
  const updateZoomRef = useRef(updateZoom);
  updateZoomRef.current = updateZoom;
  const setZoomLevelRef = useRef(setZoomLevel);
  setZoomLevelRef.current = setZoomLevel;

  // Handle viewport changes (pan/zoom)
  const handleMoveEnd = useCallback(
    (event: ViewStateChangeEvent) => {
      const map = mapRef.current;
      if (!map) return;

      const bounds = map.getBounds();
      if (!bounds) return;

      // Update zoom level for LOD filtering
      const zoom = event.viewState.zoom;
      setZoomLevel(zoom);
      updateZoom(zoom);

      // Update viewport subscription
      updateBounds({
        west: bounds.getWest(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        north: bounds.getNorth(),
      });
    },
    [updateBounds, updateZoom, setZoomLevel]
  );

  // Initialize map: register icons and send initial viewport subscription.
  // This runs from a polling interval that waits for the map ref to be available,
  // because react-map-gl's onLoad callback is unreliable with reuseMaps and
  // React 18 strict mode (double-rendering causes the event to be lost).
  useEffect(() => {
    if (initializedRef.current) return;

    const tryInitialize = async () => {
      const map = mapRef.current;
      if (!map) return false;

      let mapInstance: maplibregl.Map;
      try {
        mapInstance = map.getMap();
      } catch {
        return false;
      }

      // We only need bounds to subscribe — don't wait for full style/tile load
      const bounds = map.getBounds();
      if (!bounds) return false;

      // Map is ready — initialize
      initializedRef.current = true;

      // Register vehicle icons directly — don't gate on isStyleLoaded() because
      // it returns false when GeoJSON sources have pending updates (always true
      // when vehicle data is streaming). The styleimagemissing handler below
      // provides a safety net for any icons that fail to register here.
      try {
        await registerVehicleIcons(mapInstance);
      } catch (error) {
        console.warn('Failed to register vehicle icons:', error);
      }
      setIconsReady(true);

      // Set initial zoom and subscribe to viewport
      const zoom = map.getZoom();
      setZoomLevelRef.current(zoom);
      updateZoomRef.current(zoom);

      forceSubscribeRef.current({
        west: bounds.getWest(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        north: bounds.getNorth(),
      });

      return true;
    };

    // Try immediately, then poll briefly until the map is ready
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      const done = await tryInitialize();
      if (done || attempts > 50) {
        clearInterval(interval);
      }
    }, 100);

    // Also try immediately (before first interval tick)
    tryInitialize().then((done) => {
      if (done) clearInterval(interval);
    });

    return () => clearInterval(interval);
  }, []);

  // Re-register icons on style reload (e.g. after network reconnect or style change).
  // The initialization effect only fires once; this handles subsequent style loads.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    let mapInstance: maplibregl.Map;
    try {
      mapInstance = map.getMap();
    } catch {
      return;
    }

    const handleStyleLoad = async () => {
      setIconsReady(false);
      resetIconRegistration();
      try {
        await registerVehicleIcons(mapInstance);
      } catch (error) {
        console.warn('Failed to re-register vehicle icons on style reload:', error);
      }
      setIconsReady(true);
    };

    mapInstance.on('style.load', handleStyleLoad);
    return () => {
      mapInstance.off('style.load', handleStyleLoad);
    };
  }, []);

  // Safety net: load icons on demand when MapLibre tries to render a missing image.
  // This catches any icons missed during bulk registration (e.g. race conditions,
  // style reloads, or failed fetches). Persistent listener matches reuseMaps lifetime.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    let mapInstance: maplibregl.Map;
    try {
      mapInstance = map.getMap();
    } catch {
      return;
    }

    const handleMissingImage = (e: { id: string }) => {
      if (KNOWN_ICON_IDS.has(e.id)) {
        loadIconOnDemand(mapInstance, e.id);
      }
    };

    mapInstance.on('styleimagemissing', handleMissingImage);
    return () => {
      mapInstance.off('styleimagemissing', handleMissingImage);
    };
  }, []);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      mapRef.current?.resize();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Consume map commands from outside the Map tree (e.g. SchedulePanel click-to-pan)
  const pendingCommand = useMapCommandStore((s) => s.pendingCommand);
  const clearCommand = useMapCommandStore((s) => s.clearCommand);

  useEffect(() => {
    if (!pendingCommand || !mapRef.current) return;
    mapRef.current.flyTo({
      center: [pendingCommand.longitude, pendingCommand.latitude],
      zoom: pendingCommand.zoom ?? 15,
      duration: 1000,
    });
    clearCommand();
  }, [pendingCommand, clearCommand]);

  return (
    <div className={className} style={{ width: '100%', height: '100%' }}>
    <Map
      ref={mapRef}
      mapLib={maplibregl}
      mapStyle={mapStyle}
      initialViewState={{
        longitude: MAP_CONFIG.CENTER[0],
        latitude: MAP_CONFIG.CENTER[1],
        zoom: MAP_CONFIG.INITIAL_ZOOM,
      }}
      minZoom={MAP_CONFIG.MIN_ZOOM}
      maxZoom={MAP_CONFIG.MAX_ZOOM}
      maxBounds={MAP_CONFIG.MAX_BOUNDS}
      onMoveEnd={handleMoveEnd}
      style={{ width: '100%', height: '100%' }}
      attributionControl={false}
      reuseMaps
    >
      {/* Navigation controls */}
      <NavigationControl position="top-right" showCompass showZoom />

      {/* User geolocation control */}
      <GeolocateControl
        position="bottom-right"
        trackUserLocation
        showAccuracyCircle
      />

      {/* Attribution */}
      <AttributionControl
        position="bottom-right"
        compact
        customAttribution="&copy; OpenStreetMap contributors, &copy; CARTO"
      />

      {/* H3 hex density overlay (bottommost, admin only) */}
      {isAdmin && <HexDensityLayer />}
      {/* Hex density legend (admin only, auto-hides when overlay disabled) */}
      <HexDensityLegend />

      {/* Poller bounding box layer (below vehicles, admin only) */}
      {isAdmin && <PollerBboxLayer />}

      {/* Route polyline and stops for selected journey */}
      <RouteLayer />

      {/* V4 position layer (admin only, rendered below vehicle layer) */}
      {isAdmin && <V4PositionLayer />}

      {/* Deviation lines between GPS and V4 position (admin only) */}
      {isAdmin && <DeviationLine />}

      {/* V5 segment-interpolated position (admin only) */}
      {isAdmin && <V5PositionLayer />}

      {/* V1-DA GPS target position (logged-in users) */}
      <V1DAPositionLayer />

      {/* Position Sources crosshairs (logged-in users, independent toggles) */}
      <PositionSourcesLayer />

      {/* Main vehicle layer (confidence-based) */}
      <VehicleLayer iconsReady={iconsReady} />
    </Map>
    </div>
  );
}
